import type { AppointmentPlanningDateRangeFilter } from "../../../../../../../_logic/appointments-contract"
import type { PlanningPreviewRow } from "../../../../../_shared/_logic/table-contract"
import {
  appointmentPlanningDateFromValue,
  completeAppointmentPlanningDateRange,
  normalizeAppointmentPlanningSearch,
} from "../../../_shared/_logic/filters"
import { addDays } from "../../../../../../../../../../_shared/_logic/date-math"

export type AppointmentPlanningCalendarWindow = {
  visibleDays: Date[]
  rangeStart: Date
  rangeEnd: Date
  employeeIds: string[]
}

export const appointmentPlanningCalendarDayKey = (value: Date): string => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export const appointmentPlanningCalendarColumnId = (employeeId: string, day: Date): string =>
  `${employeeId}-${appointmentPlanningCalendarDayKey(day)}`

export const buildAppointmentPlanningCalendarDays = (start: Date, end: Date): Date[] => {
  const result: Date[] = []
  for (let day = new Date(start); day <= end; day = addDays(day, 1)) result.push(day)

  return result
}

export const buildAppointmentPlanningCalendarWindow = ({
  previewEmployeeIds,
  dateRange,
  employeeId,
}: {
  previewEmployeeIds: string[]
  dateRange: AppointmentPlanningDateRangeFilter
  employeeId: string
}): AppointmentPlanningCalendarWindow | null => {
  const range = completeAppointmentPlanningDateRange(dateRange)

  if (!range) return null

  const selectedEmployeeId = String(employeeId ?? "").trim()
  const employeeIds = previewEmployeeIds.filter(
    (candidate) => !selectedEmployeeId || candidate === selectedEmployeeId,
  )
  const rangeEnd = addDays(range.end, 1)

  return {
    visibleDays: buildAppointmentPlanningCalendarDays(range.start, range.end),
    rangeStart: range.start,
    rangeEnd,
    employeeIds,
  }
}

export const buildAppointmentPlanningCalendarVisibleColumnIds = ({
  planningRows,
  activeRowIds,
  window,
  search,
}: {
  planningRows: PlanningPreviewRow[]
  activeRowIds: Set<string>
  window: AppointmentPlanningCalendarWindow | null
  search: string
}): Set<string> | null => {
  const normalizedSearch = normalizeAppointmentPlanningSearch(search)

  if (!normalizedSearch) return null

  const result = new Set<string>()

  if (!window) return result

  const allowedEmployees = new Set(window.employeeIds)
  for (const row of planningRows) {
    if (
      !activeRowIds.has(String(row.id)) ||
      !allowedEmployees.has(String(row.appointmentEmployeeId))
    )
      continue

    if (!normalizeAppointmentPlanningSearch(row.searchText).includes(normalizedSearch)) continue

    const appointmentStart = appointmentPlanningDateFromValue(row.appointmentStartAt)

    if (
      !appointmentStart ||
      appointmentStart < window.rangeStart ||
      appointmentStart >= window.rangeEnd
    )
      continue

    result.add(appointmentPlanningCalendarColumnId(row.appointmentEmployeeId, appointmentStart))
  }

  return result
}
